import {
  type PropsWithChildren,
  useImperativeHandle,
  type Ref,
  type RefObject,
} from 'react';

import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { INTERACTION_MODE } from '../../core/components/interactions-overlay/types.js';
import { reduceInteractionMode } from '../../core/components/interactions-state-helpers/reducers/mode-reducers.js';
import { useTriggerZoom } from '../../core/components/zoom/hooks/useTriggerZoom.js';
import { ChartType } from '../../core/types/chart-types.js';
import { useApplyZoomDelta } from '../components/overlays/hooks/useApplyZoomDelta.js';
import { useZoomState } from '../hooks/store/selectors.js';
import { useSetState } from '../hooks/store/state-setters.js';
import { useHistogramData } from '../hooks/useHistogramData.js';
import { useResetInteractions } from '../hooks/useResetInteractions.js';
import type { HistogramChartConfiguration } from '../types/histogram-props.js';
import type { HistogramChartRef } from '../types/histogram-ref.js';
import { buildAndDownloadCsv } from '../utils/build-and-download-csv.js';
import { normalizeConfig } from '../utils/histogram-configuration.js';

export interface HistogramChartImperativeHandlerProps {
  forwardedRef: Ref<HistogramChartRef>;
  chartRef: RefObject<HTMLDivElement | null>;
  config: HistogramChartConfiguration;
}

export const HistogramChartImperativeHandler = ({
  forwardedRef,
  chartRef,
  config,
  children,
}: PropsWithChildren<HistogramChartImperativeHandlerProps>) => {
  const { interactions } = config;

  const setState = useSetState();

  const { width } = useGridPlotSize();
  const applyZoom = useApplyZoomDelta();
  const triggerZoom = useTriggerZoom(width, applyZoom);

  const resetInteractions = useResetInteractions();
  const { series } = useHistogramData();

  const { zoomInEnabled, zoomOutEnabled } = useZoomState();

  useImperativeHandle(forwardedRef, (): HistogramChartRef => {
    return {
      element: chartRef.current,
      getConfig: () =>
        JSON.stringify({
          type: ChartType.Histogram,
          ...normalizeConfig(config),
        }),
      downloadData: () => {
        buildAndDownloadCsv(series);
      },
      exploreMode: () => {
        setState(reduceInteractionMode(INTERACTION_MODE.Explore));
      },
      zoomMode: () => {
        if (interactions?.zoom) {
          setState(reduceInteractionMode(INTERACTION_MODE.Zoom));
        }
      },
      panMode: () => {
        if (interactions?.pan && zoomOutEnabled) {
          setState(reduceInteractionMode(INTERACTION_MODE.Pan));
        }
      },
      zoomIn: () => {
        if (zoomInEnabled) {
          triggerZoom('zoom-in');
        }
      },
      zoomOut: () => {
        if (zoomOutEnabled) {
          triggerZoom('zoom-out');
        }
      },
      reset: () => {
        resetInteractions();
      },
    };
  });

  return children;
};
